import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Building, 
  Plus, 
  Pencil, 
  Users,
  Loader2,
  Save,
  Key,
  BarChart3,
  TrendingUp,
  CreditCard,
  Activity,
  Globe,
  UserPlus,
  Settings,
  Shield,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Send,
  AlertCircle,
  Check
} from 'lucide-react';

export default function SuperAdminPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [companies, setCompanies] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    totalEmployees: 0,
    activeCampaigns: 0,
    monthlyGrowth: 0
  });
  const [companyDialog, setCompanyDialog] = useState({ open: false, company: null });
  const [adminDialog, setAdminDialog] = useState({ open: false, companyId: null });
  const [companyForm, setCompanyForm] = useState({ name: '', logoUrl: '', mainColor: '#2054A6' });
  const [adminForm, setAdminForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'Admin' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Email provider config state
  const [emailConfig, setEmailConfig] = useState({
    provider: 'sendgrid',
    sendgridApiKey: '',
    microsoft365TenantId: '',
    microsoft365ClientId: '',
    microsoft365ClientSecret: '',
    senderEmail: '',
    senderName: 'HR Engine',
    isConfigured: false
  });
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  useEffect(() => {
    fetchData();
    fetchEmailConfig();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      
      // Calculate global stats
      const totalUsers = response.data.reduce((acc, c) => acc + (c.userCount || 0), 0);
      const totalEmployees = response.data.reduce((acc, c) => acc + (c.employeeCount || 0), 0);
      
      setGlobalStats({
        totalCompanies: response.data.length,
        totalUsers,
        totalEmployees,
        activeCampaigns: 0, // Will be fetched separately if needed
        monthlyGrowth: 12 // Placeholder
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCompanyDialog = (company = null) => {
    setCompanyForm({
      name: company?.name || '',
      logoUrl: company?.logoUrl || '',
      mainColor: company?.mainColor || '#2054A6'
    });
    setCompanyDialog({ open: true, company });
  };

  const saveCompany = async () => {
    if (!companyForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (companyDialog.company) {
        await api.put(`/companies/${companyDialog.company.id}`, companyForm);
        toast.success('Entreprise mise à jour');
      } else {
        await api.post('/companies', companyForm);
        toast.success('Entreprise créée');
      }
      fetchData();
      setCompanyDialog({ open: false, company: null });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const openAdminDialog = (companyId) => {
    setAdminForm({ firstName: '', lastName: '', email: '', password: '', role: 'Admin' });
    setAdminDialog({ open: true, companyId });
  };

  const createAdmin = async () => {
    if (!adminForm.firstName || !adminForm.lastName || !adminForm.email || !adminForm.password) {
      toast.error('Tous les champs sont requis');
      return;
    }
    setSaving(true);
    try {
      await api.post('/users', {
        ...adminForm,
        companyId: adminDialog.companyId
      });
      toast.success('Utilisateur créé avec succès');
      setAdminDialog({ open: false, companyId: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/seed');
      toast.success('Données de démonstration créées');
      fetchData();
      
      if (response.data.credentials) {
        toast.info(`SuperAdmin: ${response.data.credentials.superadmin.email}`, { duration: 10000 });
        toast.info(`Admin: ${response.data.credentials.admin.email}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Des données existent déjà');
    } finally {
      setSeeding(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchEmailConfig = async () => {
    try {
      const response = await api.get('/admin/emails/config/provider');
      if (response.data) {
        setEmailConfig(prev => ({
          ...prev,
          ...response.data,
          sendgridApiKey: response.data.sendgridApiKey || '',
          microsoft365ClientSecret: response.data.microsoft365ClientSecret || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const saveEmailConfig = async () => {
    setSavingEmailConfig(true);
    try {
      await api.put('/admin/emails/config/provider', emailConfig);
      toast.success('Configuration email enregistrée');
      fetchEmailConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const testEmailSend = async () => {
    if (!testEmailAddress) {
      toast.error('Veuillez entrer une adresse email de test');
      return;
    }
    setTestingEmail(true);
    try {
      await api.post('/admin/emails/test', { email: testEmailAddress });
      toast.success(`Email de test envoyé à ${testEmailAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Administration">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Administration">
      <div className="space-y-6" data-testid="superadmin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-webtech-blue" />
              Administration Globale
            </h1>
            <p className="text-slate-600">
              Gestion centralisée de toutes les entreprises et utilisateurs
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            {companies.length === 0 && (
              <Button 
                variant="outline" 
                onClick={handleSeedData}
                disabled={seeding}
                data-testid="seed-data-btn"
              >
                {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                Créer données démo
              </Button>
            )}
            <Button 
              onClick={() => openCompanyDialog()} 
              className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
              data-testid="add-company-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle entreprise
            </Button>
          </div>
        </div>

        {/* Global Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Entreprises</p>
                  <p className="text-2xl font-bold text-slate-900">{globalStats.totalCompanies}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Utilisateurs</p>
                  <p className="text-2xl font-bold text-slate-900">{globalStats.totalUsers}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Employés</p>
                  <p className="text-2xl font-bold text-slate-900">{globalStats.totalEmployees}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <UserPlus className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Campagnes actives</p>
                  <p className="text-2xl font-bold text-slate-900">{globalStats.activeCampaigns}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Activity className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-webtech-blue to-webtech-blue-hover text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80">Croissance</p>
                  <p className="text-2xl font-bold">+{globalStats.monthlyGrowth}%</p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 w-full max-w-2xl">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="companies">Entreprises</TabsTrigger>
            <TabsTrigger value="subscriptions">Abonnements</TabsTrigger>
            <TabsTrigger value="emails">Emails</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Companies */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Entreprises récentes</CardTitle>
                  <CardDescription>Dernières entreprises créées</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {companies.slice(0, 5).map((company) => (
                      <div key={company.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: company.mainColor || '#2054A6' }}
                          >
                            {company.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-xs text-slate-500">
                              {company.employeeCount || 0} employés • {company.userCount || 0} utilisateurs
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => openCompanyDialog(company)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {companies.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Building className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Aucune entreprise créée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Actions rapides</CardTitle>
                  <CardDescription>Opérations courantes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => openCompanyDialog()}
                    >
                      <Building className="w-6 h-6 text-blue-600" />
                      <span>Nouvelle entreprise</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => setActiveTab('companies')}
                    >
                      <Users className="w-6 h-6 text-green-600" />
                      <span>Gérer utilisateurs</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => setActiveTab('subscriptions')}
                    >
                      <CreditCard className="w-6 h-6 text-purple-600" />
                      <span>Abonnements</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => setActiveTab('settings')}
                    >
                      <Settings className="w-6 h-6 text-orange-600" />
                      <span>Paramètres</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Activity Log Placeholder */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activité récente</CardTitle>
                <CardDescription>Journal des dernières actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companies.slice(0, 3).map((company, idx) => (
                    <div key={company.id} className="flex items-center gap-3 p-2 border-b last:border-0">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Building className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{company.name}</span> a été {idx === 0 ? 'créée' : 'mise à jour'}
                        </p>
                        <p className="text-xs text-slate-500">{company.createdAt ? new Date(company.createdAt).toLocaleDateString('fr-FR') : 'Date inconnue'}</p>
                      </div>
                    </div>
                  ))}
                  {companies.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Aucune activité récente</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-4">
            {/* Search and filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher une entreprise..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filtres
              </Button>
            </div>

            {/* Companies Table */}
            <Card>
              <CardHeader>
                <CardTitle>Entreprises</CardTitle>
                <CardDescription>Liste de toutes les entreprises clientes ({filteredCompanies.length})</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Utilisateurs</TableHead>
                      <TableHead>Employés</TableHead>
                      <TableHead>Abonnement</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                          <Building className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p>Aucune entreprise trouvée</p>
                          {companies.length === 0 && (
                            <p className="text-sm mt-2">Créez une entreprise ou utilisez les données de démonstration</p>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCompanies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: company.mainColor || '#2054A6' }}
                              >
                                {company.name?.[0]?.toUpperCase()}
                              </div>
                              <div>
                                <span className="font-medium">{company.name}</span>
                                <p className="text-xs text-slate-500">{company.mainColor}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{company.userCount || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{company.employeeCount || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-700">BUSINESS</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-600">Actif</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openCompanyDialog(company)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openAdminDialog(company.id)}
                                title="Créer un utilisateur"
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des abonnements</CardTitle>
                <CardDescription>Vue d'ensemble des plans et paiements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card className="border-2 border-slate-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-500">STARTER</p>
                        <p className="text-3xl font-bold mt-2">0 FCFA</p>
                        <p className="text-xs text-slate-500">par mois</p>
                        <div className="mt-4">
                          <Badge variant="outline">{companies.filter(c => !c.subscription || c.subscription === 'free').length} entreprises</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-webtech-blue">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Badge className="bg-webtech-blue mb-2">Populaire</Badge>
                        <p className="text-sm font-medium text-slate-500">BUSINESS</p>
                        <p className="text-3xl font-bold mt-2">25 000 FCFA</p>
                        <p className="text-xs text-slate-500">par mois</p>
                        <div className="mt-4">
                          <Badge className="bg-webtech-blue/10 text-webtech-blue">{companies.filter(c => c.subscription === 'pro').length} entreprises</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-purple-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-500">Enterprise</p>
                        <p className="text-3xl font-bold mt-2">Sur mesure</p>
                        <p className="text-xs text-slate-500">contactez-nous</p>
                        <div className="mt-4">
                          <Badge className="bg-purple-100 text-purple-700">{companies.filter(c => c.subscription === 'enterprise').length} entreprises</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="border rounded-lg p-6 bg-amber-50 border-amber-200">
                  <div className="flex items-start gap-3">
                    <CreditCard className="w-6 h-6 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Intégration PayTech Africa</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        L'intégration du système de paiement PayTech Africa est en cours de développement. 
                        Les fonctionnalités de facturation automatique et de gestion des abonnements seront bientôt disponibles.
                      </p>
                      <Button variant="outline" size="sm" className="mt-3" disabled>
                        <Clock className="w-4 h-4 mr-2" />
                        Bientôt disponible
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Configuration des emails transactionnels
                </CardTitle>
                <CardDescription>
                  Configurez le service d'envoi d'emails (SendGrid ou Microsoft 365 Graph API)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Provider Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Fournisseur d'emails</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        emailConfig.provider === 'sendgrid' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setEmailConfig({ ...emailConfig, provider: 'sendgrid' })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                          SG
                        </div>
                        <div>
                          <p className="font-medium">SendGrid</p>
                          <p className="text-sm text-slate-500">Service email cloud</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        emailConfig.provider === 'microsoft365' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setEmailConfig({ ...emailConfig, provider: 'microsoft365' })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0078d4] rounded-lg flex items-center justify-center text-white font-bold text-xs">
                          M365
                        </div>
                        <div>
                          <p className="font-medium">Microsoft 365</p>
                          <p className="text-sm text-slate-500">Graph API</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SendGrid Config */}
                {emailConfig.provider === 'sendgrid' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Configuration SendGrid
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sendgrid-key">Clé API SendGrid</Label>
                        <Input
                          id="sendgrid-key"
                          type="password"
                          placeholder="SG.xxxxx..."
                          value={emailConfig.sendgridApiKey}
                          onChange={(e) => setEmailConfig({ ...emailConfig, sendgridApiKey: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sender-email-sg">Email expéditeur</Label>
                        <Input
                          id="sender-email-sg"
                          type="email"
                          placeholder="noreply@votreentreprise.com"
                          value={emailConfig.senderEmail}
                          onChange={(e) => setEmailConfig({ ...emailConfig, senderEmail: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Microsoft 365 Config */}
                {emailConfig.provider === 'microsoft365' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Configuration Microsoft 365 Graph API
                    </h4>
                    <p className="text-sm text-slate-600">
                      Pour configurer Microsoft Graph API, vous devez créer une application dans Azure AD.
                      <a 
                        href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline ml-1"
                      >
                        Accéder au portail Azure →
                      </a>
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="m365-tenant">Tenant ID (Directory ID)</Label>
                        <Input
                          id="m365-tenant"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={emailConfig.microsoft365TenantId}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365TenantId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-client">Client ID (Application ID)</Label>
                        <Input
                          id="m365-client"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={emailConfig.microsoft365ClientId}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365ClientId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-secret">Client Secret</Label>
                        <Input
                          id="m365-secret"
                          type="password"
                          placeholder="Votre secret client..."
                          value={emailConfig.microsoft365ClientSecret}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365ClientSecret: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-sender">Email expéditeur (Microsoft 365)</Label>
                        <Input
                          id="m365-sender"
                          type="email"
                          placeholder="notifications@votreentreprise.com"
                          value={emailConfig.senderEmail}
                          onChange={(e) => setEmailConfig({ ...emailConfig, senderEmail: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Permissions requises :</strong> L'application Azure AD doit avoir la permission 
                        <code className="bg-amber-100 px-1 mx-1 rounded">Mail.Send</code> 
                        (Application permission, pas Delegated).
                      </p>
                    </div>
                  </div>
                )}

                {/* Common Config */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium">Paramètres généraux</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sender-name">Nom de l'expéditeur</Label>
                      <Input
                        id="sender-name"
                        placeholder="HR Engine"
                        value={emailConfig.senderName}
                        onChange={(e) => setEmailConfig({ ...emailConfig, senderName: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    {emailConfig.isConfigured ? (
                      <>
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-green-600">Configuration active</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-amber-600">Non configuré</span>
                      </>
                    )}
                  </div>
                  <Button 
                    onClick={saveEmailConfig} 
                    disabled={savingEmailConfig}
                    className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                  >
                    {savingEmailConfig ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer la configuration
                  </Button>
                </div>

                {/* Test Email */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Tester la configuration</h4>
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      className="max-w-xs"
                    />
                    <Button 
                      variant="outline" 
                      onClick={testEmailSend}
                      disabled={testingEmail || !testEmailAddress}
                    >
                      {testingEmail ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Envoyer un test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Configuration générale</CardTitle>
                  <CardDescription>Paramètres globaux de la plateforme</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Mode maintenance</p>
                      <p className="text-sm text-slate-500">Désactive l'accès pour les utilisateurs</p>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-200">Désactivé</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Notifications email</p>
                      <p className="text-sm text-slate-500">Envoi des emails transactionnels</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Actif</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Inscriptions ouvertes</p>
                      <p className="text-sm text-slate-500">Permettre les nouvelles inscriptions</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Actif</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Intégrations</CardTitle>
                  <CardDescription>Services connectés</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Globe className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">SendGrid</p>
                        <p className="text-sm text-slate-500">Emails transactionnels</p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Connecté</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Shield className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Azure AD</p>
                        <p className="text-sm text-slate-500">Synchronisation employés</p>
                      </div>
                    </div>
                    <Badge variant="outline">Optionnel</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <CreditCard className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">PayTech Africa</p>
                        <p className="text-sm text-slate-500">Paiements</p>
                      </div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Company Dialog */}
        <Dialog open={companyDialog.open} onOpenChange={(open) => setCompanyDialog({ open, company: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{companyDialog.company ? 'Modifier' : 'Nouvelle'} entreprise</DialogTitle>
              <DialogDescription>
                {companyDialog.company ? 'Modifiez les informations de l\'entreprise' : 'Créez une nouvelle entreprise cliente'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de l'entreprise *</Label>
                <Input
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="Ex: Webtech Mali SARL"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL du logo (optionnel)</Label>
                <Input
                  value={companyForm.logoUrl}
                  onChange={(e) => setCompanyForm({ ...companyForm, logoUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Couleur principale</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={companyForm.mainColor}
                    onChange={(e) => setCompanyForm({ ...companyForm, mainColor: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={companyForm.mainColor}
                    onChange={(e) => setCompanyForm({ ...companyForm, mainColor: e.target.value })}
                    placeholder="#2054A6"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompanyDialog({ open: false, company: null })}>
                Annuler
              </Button>
              <Button onClick={saveCompany} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin/User Creation Dialog */}
        <Dialog open={adminDialog.open} onOpenChange={(open) => setAdminDialog({ open, companyId: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un utilisateur</DialogTitle>
              <DialogDescription>
                Ajoutez un nouvel utilisateur pour cette entreprise
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={adminForm.firstName}
                    onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={adminForm.lastName}
                    onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={adminForm.role} onValueChange={(v) => setAdminForm({ ...adminForm, role: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SuperAdmin">SuperAdmin</SelectItem>
                    <SelectItem value="Admin">Admin RH</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Employé">Employé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminDialog({ open: false, companyId: null })}>
                Annuler
              </Button>
              <Button onClick={createAdmin} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Créer l'utilisateur
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
